import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, Check, X, Settings } from "lucide-react";
import { getJSON, setJSON } from "@/lib/safe-storage";

const STORAGE_KEY = "teebee_cookie_consent_v1";

// NOTE: consent flags only — never PII, never auth state.
function readConsent() {
  return getJSON(STORAGE_KEY, null);
}

function writeConsent(value) {
  setJSON(STORAGE_KEY, { ...value, at: new Date().toISOString() });
}

/**
 * Cookie consent banner — appears once per device until accepted/rejected.
 *
 * Consent shape: { essential: true, preference, analytics, performance, at }
 *
 * Other parts of the app can read this via `getCookieConsent()` to decide
 * whether to load non-essential analytics scripts.
 */
export default function CookieConsent() {
  // Lazy initializer — avoids the `set-state-in-effect` lint warning while still
  // letting localStorage decide whether to show the banner on first render.
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return !readConsent();
  });
  const [advanced, setAdvanced] = useState(false);
  const [prefs, setPrefs] = useState({ preference: true, analytics: true, performance: true });

  const acceptAll = () => {
    writeConsent({ essential: true, preference: true, analytics: true, performance: true });
    setOpen(false);
  };
  const rejectAll = () => {
    writeConsent({ essential: true, preference: false, analytics: false, performance: false });
    setOpen(false);
  };
  const saveCustom = () => {
    writeConsent({ essential: true, ...prefs });
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-4 sm:bottom-4 sm:max-w-md z-50 rounded-2xl border border-border bg-card shadow-2xl backdrop-blur p-5"
    >
      <div className="flex items-start gap-3">
        <span className="inline-grid place-items-center h-9 w-9 rounded-xl bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0">
          <Cookie className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">We use a few cookies.</p>
          <p className="mt-1 text-[12px] text-muted-foreground leading-relaxed">
            Essential cookies keep you signed in. With your consent we add preference, analytics and performance cookies to improve TeeBee.
            See our <Link to="/legal/cookie-policy" className="underline hover:text-foreground">Cookie Policy</Link>.
          </p>

          {advanced && (
            <div className="mt-3 space-y-2 rounded-xl border border-border bg-muted/40 p-3" data-testid="cookie-prefs">
              <Row label="Essential" hint="Required — can't be disabled." checked disabled />
              <Row label="Preference" hint="Remember language, theme, dismissed notices." checked={prefs.preference} onChange={(v) => setPrefs((p) => ({ ...p, preference: v }))} />
              <Row label="Analytics" hint="Anonymous aggregate usage." checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
              <Row label="Performance" hint="Page-load and error metrics." checked={prefs.performance} onChange={(v) => setPrefs((p) => ({ ...p, performance: v }))} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={acceptAll} size="sm" className="rounded-full" data-testid="cookie-accept">
              <Check className="h-3.5 w-3.5 mr-1.5" /> Accept all
            </Button>
            <Button onClick={rejectAll} variant="outline" size="sm" className="rounded-full" data-testid="cookie-reject">
              <X className="h-3.5 w-3.5 mr-1.5" /> Reject non-essential
            </Button>
            {advanced ? (
              <Button onClick={saveCustom} variant="ghost" size="sm" className="rounded-full" data-testid="cookie-save">
                Save preferences
              </Button>
            ) : (
              <Button onClick={() => setAdvanced(true)} variant="ghost" size="sm" className="rounded-full text-muted-foreground" data-testid="cookie-customize">
                <Settings className="h-3.5 w-3.5 mr-1.5" /> Customise
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, hint, checked, onChange, disabled }) {
  return (
    <label className={`flex items-start gap-2 text-[12px] cursor-pointer ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => !disabled && onChange?.(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 h-3.5 w-3.5 accent-violet-600"
      />
      <span className="flex-1">
        <span className="font-medium">{label}</span>
        <span className="block text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

export function getCookieConsent() { return readConsent(); }
